/* eslint-disable max-len */

import { Application, BasePlugin, Device } from '@goodboydigital/astro';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';

export interface AnalyticsMetadata
{
    /* ---------- GENERAL METADATA ---------- */
    race_number: any,
    page_name: string,
    game_title: string,
    game_level: string,
    game_category: string,
    game_character: string,
    brand: string,
    section: string,
    subsection: string,
    content_type: string,
    market_region: string,
    market: string,
    version: string,
    user_lang: string,
    english_name: string,
    game_feature: string,
    
    app_name?: string,
    domain?: string,
    
    /* ---------- EVENT METADATA ---------- */
    interaction?: string,
    interaction_event?: number,
    pageview_event?: number,
    game_item?: string,
    gameloadduration?: string,
    gamestart_event?: number,
    gameplay_event?: number,
    levelstart_event?: number,
    levelcomplete_event?: number,
    tutorialstart_event?: number,
    tutorialplay_event?: number,
    tutorialpause_event?: number,
    tutorialcomplete_event?: number,
    tutorialreplay_event?: number,
    game_level_pause_event?: number,
    game_exit_event?: number,
    gamecomplete_event?: number,
    game_level_replay_event?: number,
    gamemilestone_name?: string,
    gameadclick?: number,
    gametimespent_event?: number,
    gamemilestones?: number,
    
    //For turner metadata use only
    type?: string,
    
}

export interface AnalyticsData
{
    id: string,
    //Metadata that persists for future events/states
    persistentMetaData?: Partial<AnalyticsMetadata>
    //Metadata that is only set for this event
    additionalMetadata?: Partial<AnalyticsMetadata>
}

export class AnalyticsEvent
{
    static readonly GAME_START: AnalyticsData = { id: 'GAME START', additionalMetadata: { interaction: "game Start", gamestart_event: 1, interaction_event: 1 } };
    static readonly GAME_PLAY: AnalyticsData = { id: 'GAME PLAY', persistentMetaData: { section: "race", subsection: "race|race", content_type: "race" }, additionalMetadata: { interaction: "game play", gameplay_event: 1, interaction_event: 1 } };
    static readonly GAME_LEVEL_START: AnalyticsData = { id: 'GAME LEVEL START', additionalMetadata: { interaction: "level start", levelstart_event: 1, interaction_event: 1 } };
    static readonly GAME_LEVEL_COMPLETE: AnalyticsData = { id: 'GAME LEVEL COMPLETE', additionalMetadata: { interaction: "level complete", levelcomplete_event: 1, interaction_event: 1 } };
    static readonly GAME_PAUSE: AnalyticsData = { id: 'GAME PAUSE', additionalMetadata: { interaction: "game level pause", game_level_pause_event: 1, interaction_event: 1 } };
    static readonly GAME_EXIT: AnalyticsData = { id: 'GAME EXIT', additionalMetadata: { interaction: "game exit", game_exit_event: 1, interaction_event: 1 } };
    static readonly GAME_COMPLETE: AnalyticsData = { id: 'GAME COMPLETE', additionalMetadata: { interaction: "game complete", gamecomplete_event: 1, interaction_event: 1 } };
    static readonly GAME_REPLAY: AnalyticsData = { id: 'GAME REPLAY', additionalMetadata: { interaction: "game level replay", game_level_replay_event: 1, interaction_event: 1 } };
    static readonly TUTORIAL_PLAY: AnalyticsData = { id: 'TUTORIAL PLAY', additionalMetadata: { interaction: "tutorial play", tutorialplay_event: 1, interaction_event: 1 } };
    static readonly TUTORIAL_START: AnalyticsData = { id: 'TUTORIAL START', additionalMetadata: { interaction: "tutorial start", tutorialstart_event: 1, interaction_event: 1 } };
    static readonly TUTORIAL_COMPLETE: AnalyticsData = { id: 'TUTORIAL COMPLETE', additionalMetadata: { interaction: "tutorial complete", tutorialcomplete_event: 1, interaction_event: 1 } };
    static readonly TUTORIAL_PAUSE: AnalyticsData = { id: 'TUTORIAL PAUSE', additionalMetadata: { interaction: "tutorial pause", tutorialpause_event: 1, interaction_event: 1 } };
    static readonly TUTORIAL_REPLAY: AnalyticsData = { id: 'TUTORIAL REPLAY', additionalMetadata: { interaction: "tutorial replay", tutorialreplay_event: 1, interaction_event: 1 } };
    static readonly CONTENT_UNLOCKED: AnalyticsData = { id: 'CONTENT UNLOCKED', additionalMetadata: { interaction: "content unlocked", gamemilestone_name: "content unlocked", game_feature: "content purchase", gamemilestones: 1, interaction_event: 1 } };
    static readonly PURCHASED_ALL_ITEMS: AnalyticsData = { id: 'USER PURCHASED ALL ITEMS', additionalMetadata: { interaction: "all items purchased", gamemilestone_name: "all items purchased", game_feature: "content purchase", gamemilestones: 1, interaction_event: 1 } };
    static readonly GAME_TIME_SPENT_30_SECS: AnalyticsData = { id: 'GAME TIME SPENT - 30 SECONDS', additionalMetadata: { interaction: "game milestone", gamemilestone_name: "30 seconds gameplay", gamemilestones: 1, gametimespent_event: 1 } };
    static readonly HOMEPAGE_CHANGEID_CLICK: AnalyticsData = { id: 'HOME PAGE: CHANGE ID CLICK', persistentMetaData: { content_type: "changeid" }, additionalMetadata: { interaction: "home|changeid click", game_feature: "changeid", interaction_event: 1 } };
    static readonly HOMEPAGE_SETTINGS_CLICK: AnalyticsData = { id: 'HOME PAGE: SETTINGS CLICK', additionalMetadata: { interaction: "home|settings click", game_item: "settings click", game_feature: "settings", interaction_event: 1 } };
    static readonly SETTINGS_MENU_SELECTION_CLICK: AnalyticsData = { id: 'SETTINGS: MENU SELECTION CLICK', additionalMetadata: { interaction: "settings|menu click", game_feature: "settings", interaction_event: 1 } };
    static readonly HOMEPAGE_CUSTOMISE_CLICK: AnalyticsData = { id: 'HOME PAGE: CUSTOMISE CLICK', additionalMetadata: { interaction: "home|customise click", game_item: "customise|customise", game_feature: "customise", interaction_event: 1 } };
    static readonly HOMEPAGE_RACE_CLICK: AnalyticsData = { id: 'HOME PAGE: RACE CLICK', persistentMetaData: { content_type: "race" }, additionalMetadata: { interaction: "home|race click", game_item: "race|race", game_feature: "race", interaction_event: 1 } };
    static readonly HOMEPAGE_REWARDCLAIM_CLICK: AnalyticsData = { id: 'HOME PAGE: REWARD CLAIM CLICK', additionalMetadata: { interaction: "home|reward claim click", game_feature: "reward claim", interaction_event: 1 } };
    static readonly CUSTOMISEPAGE_ITEMSELECTION_CLICK: AnalyticsData = { id: 'CUSTOMISE PAGE: ITEM SELECTION CLICK', additionalMetadata: { interaction: "customise|item selection click", game_feature: "item selection", interaction_event: 1 } };
    static readonly CUSTOMISEPAGE_RACE_CLICK: AnalyticsData = { id: 'CUSTOMISE PAGE: RACE CLICK', persistentMetaData: { content_type: "race" }, additionalMetadata: { interaction: "customise|race click", game_item: "race|race", game_feature: "race", interaction_event: 1 } };
    static readonly CHARACTERSELECTPAGE_RACE_CLICK: AnalyticsData = { id: 'CHARACTERS SELECT PAGE: RACE CLICK', persistentMetaData: { content_type: "race" }, additionalMetadata: { interaction: "character select|race click", game_item: "characters|race", game_feature: "race", interaction_event: 1 } };
    static readonly GARAGEENTERPAGE_RACE_CLICK: AnalyticsData = { id: 'GARAGE ENTER PAGE: RACE CLICK', persistentMetaData: { content_type: "race" }, additionalMetadata: { interaction: "garage enter|race click", game_item: "garage|race", interaction_event: 1 } };
    static readonly GAME_LOADDURATION: AnalyticsData = { id: 'GAME LOAD DURATION' };
    static readonly RACE_RACEPERK_CLICK: AnalyticsData = { id: 'RACE: RACE PERK CLICK', additionalMetadata: { game_feature: "race perk", interaction: "race|race perk click", interaction_event: 1 } };
    static readonly RACE_RACEREWARD_CLICK: AnalyticsData = { id: 'RACE: RACE REWARD CLICK', additionalMetadata: { game_feature: "race reward", interaction: "race|race reward click", interaction_event: 1 } };
    // To be implemented
    static readonly  RACE_ADACCEPTANCE_CLICK: AnalyticsData = { id: 'RACE AD ACCEPTANCE CLICK', additionalMetadata: { interaction: "ad acceptance click", game_feature: "race ad acceptance", gameadclick: 1, interaction_event: 1 } };
};

export class AnalyticsState 
{
    static readonly HOMEPAGE: AnalyticsData =  { id: 'HOMEPAGE', persistentMetaData: { "page_name": "home", section: "home", subsection: "home|main", content_type: "home" }, additionalMetadata: { pageview_event: 1 } };
    static readonly CUSTOMISE: AnalyticsData =  { id: 'CUSTOMISE', persistentMetaData: { "page_name": "customise", section: "customise", subsection: "customise|main", content_type: "customise" }, additionalMetadata: { pageview_event: 1 } };
    static readonly CHARACTER_SELECT: AnalyticsData =  { id: 'CHARACTERS SELECT', persistentMetaData: { "page_name": "characters select", section: "customise", subsection: "customise|characters", content_type: "characters" }, additionalMetadata: { pageview_event: 1 } };
    static readonly GARAGE_ENTER: AnalyticsData =  { id: 'GARAGE ENTER', persistentMetaData: { "page_name": "garage enter", section: "customise", subsection: "customise|garage", content_type: "garage" }, additionalMetadata: { pageview_event: 1 } };
    static readonly BODY: AnalyticsData =  { id: 'BODY', persistentMetaData: { "page_name": "body select", section: "customise", subsection: "customise|garage|body", content_type: "body" }, additionalMetadata: { pageview_event: 1 } };
    static readonly WHEELS: AnalyticsData =  { id: 'WHEELS', persistentMetaData: { "page_name": "wheels select", section: "customise", subsection: "customise|garage|wheels", content_type: "wheels" }, additionalMetadata: { pageview_event: 1 } };
    static readonly TRAILS: AnalyticsData =  { id: 'TRAILS', persistentMetaData: { "page_name": "trails select", section: "customise", subsection: "customise|garage|trails", content_type: "trails" }, additionalMetadata: { pageview_event: 1 } };
    static readonly EXTRA: AnalyticsData =  { id: 'EXTRA', persistentMetaData: { "page_name": "extra select", section: "customise", subsection: "customise|garage|extra", content_type: "extra options" }, additionalMetadata: { pageview_event: 1 } };
    static readonly TUTORIAL: AnalyticsData = { id: 'TUTORIAL', persistentMetaData: { "page_name": "tutorial", section: "tutorial", subsection: "tutorial|main", content_type: "tutorial" }, additionalMetadata: { pageview_event: 1 }};
    static readonly RACE_SELECT: AnalyticsData =  { id: 'RACE SELECT', persistentMetaData: { "page_name": "race select", section: "race", subsection: "race|race select", content_type: "race select" }, additionalMetadata: { pageview_event: 1 } };
    static readonly RACE_PLAY: AnalyticsData =  { id: 'RACE PLAY', persistentMetaData: { "page_name": "race play", section: "race", subsection: "race|race play", content_type: "race play" }, additionalMetadata: { pageview_event: 1 } };
    static readonly RACE_RESULTS: AnalyticsData =  { id: 'RACE RESULTS', persistentMetaData: { "page_name": "race results", section: "race", subsection: "race|race results", content_type: "race results" }, additionalMetadata: { pageview_event: 1 } };
    static readonly RACE_REWARDS: AnalyticsData =  { id: 'RACE REWARDS', persistentMetaData: { "page_name": "race rewards", section: "race", subsection: "race|race rewards", content_type: "race rewards" }, additionalMetadata: { pageview_event: 1 } };
};

export interface CountryData
{
    country: string,
    countryShort: string,
    region: "emea" | "latam" | "apac",
    domain: string,
}

export const COUNTRY_VALUES: Record<string, CountryData> = {
    en_GB: { country: 'united kingdom', countryShort: 'uk', region: "emea", domain: 'cartoonnetwork.co.uk' },
    de_DE: { country: 'germany', countryShort: 'de', region: 'emea', domain: 'cartoonnetwork.de' },
    it_IT: { country: 'italy', countryShort: 'it', region: 'emea', domain: 'cartoonnetwork.it' },
    es_ES: { country: 'spain', countryShort: 'es', region: 'emea', domain: 'cartoonnetwork.es' },
    fr_FR: { country: 'france', countryShort: 'fr', region: 'emea', domain: 'cartoonnetwork.fr' },
    ro_RO: { country: 'romania', countryShort: 'ro', region: 'emea', domain: 'cartoonnetwork.ro' },
    nl_NL: { country: 'netherlands', countryShort: 'nl', region: 'emea', domain: 'cartoonnetwork.nl' },
    sv_SE: { country: 'sweden', countryShort: 'se', region: 'emea', domain: 'cartoonnetwork.se' },
    da_DK: { country: 'denmark', countryShort: 'dk', region: 'emea', domain: 'cartoonnetwork.dk' },
    pl_PL: { country: 'poland', countryShort: 'pl', region: 'emea', domain: 'cartoonnetwork.pl' },
    ru_RU: { country: 'russian federation', countryShort: 'ru', region: 'emea', domain: 'cartoonnetwork.ru' },
    hu_HU: { country: 'hungary', countryShort: 'hu', region: 'emea', domain: 'cartoonnetwork.hu' },
    no_NO: { country: 'norway', countryShort: 'no', region: 'emea', domain: 'cartoonnetwork.no' },
    ar_AR: { country: 'united arab emirates', countryShort: 'ae', region: 'emea', domain: 'cartoonnetworkme.com' },
    cs_CZ: { country: 'czech republic', countryShort: 'cz', region: 'emea', domain: 'cartoonnetwork.cz' },
    bg_BG: { country: 'bulgaria', countryShort: 'bg', region: 'emea', domain: 'cartoonnetwork.bg' },
    pt_PT: { country: 'portugal', countryShort: 'pt', region: 'emea', domain: 'cartoonnetwork.pt' },
    tr_TR: { country: 'turkey', countryShort: 'tr', region: 'emea', domain: 'cartoonnetwork.com.tr' },
    pt_BR: { country: 'brazil', countryShort: 'br', region: 'latam', domain: 'cartoonnetwork.com.br'},
    es_LA: { country: 'latam', countryShort: 'la', region: 'latam', domain: 'cartoonnetworkla.com'},
    
    en_AE: { country: 'united arab emirates', countryShort: 'ae', region: 'emea', domain: 'cartoonnetworkme.com' },
    en_ZA: { country: 'south africa', countryShort: 'za', region: 'emea', domain: 'cartoonnetworkhq.com' },
    en_AU: { country: 'australia', countryShort: 'au', region: 'apac', domain: 'cartoonnetwork.com.au' },
    hi_IN: { country: 'india', countryShort: 'in', region: 'apac', domain: 'cartoonnetworkindia.com' },
    es_AR: { country: 'argentina', countryShort: 'ar', region: 'latam', domain: 'cartoonnetwork.com.ar' },
    es_VE: { country: 'venezuela', countryShort: 've', region: 'latam', domain: 'cartoonnetwork.com.ve' },
    es_MX: { country: 'mexico', countryShort: 'mx', region: 'latam', domain: 'cartoonnetwork.com.mx' },
    es_CL: { country: 'chile', countryShort: 'cl', region: 'latam', domain: 'cartoonnetwork.cl' },
    es_CO: { country: 'colombia', countryShort: 'co', region: 'latam', domain: 'cartoonnetwork.com.co' },
    en_SA: { country: 'asia', countryShort: 'sa', region: 'apac', domain: 'cartoonnetworkasia.com' },
    zh_TW: { country: 'asia', countryShort: 'tw', region: 'apac', domain: 'cartoonnetworkasia.com' },
    ko_KR: { country: 'asia', countryShort: 'kr', region: 'apac', domain: 'cartoonnetworkasia.com' }
    
};

/**
 * Analytics plugin, based on Adobe ACP Analytics
 * https://github.com/adobe/cordova-acpanalytics
 */
export class AnalyticsPlugin implements BasePlugin
{
    public app: App;
    public analyticsVersion = '';
    public acpCoreVersion = '';
    public baseData!: AnalyticsMetadata;
    private domainPrefix = '';
    private localeData: CountryData;
    private acpInitialised = false;
    
    private logInfo: boolean = false;

    private currentState?: AnalyticsData;

    constructor(app: Application)
    {
        this.app = app as App;
        
        let version = `app|${_VERSION_}`;
        
        this.localeData = COUNTRY_VALUES["GB"];
        
        this.domainPrefix = Settings.isApp() ? "app" : "web";
        
        this.baseData = {
            race_number: "nvs",
            page_name: "nvs",
            game_title: Settings.browserLite ? "cn bmx champions beta" : "cn bmx champions",
            game_level: "nvs",
            game_category: "racing",
            game_character: "nvs",
            brand: "cartoon network",
            section: "nvs",
            subsection: "nvs",
            content_type: "nvs",
            version: version,
            english_name: Settings.browserLite ? "cn bmx champions beta" : "cn bmx champions",
            market: "nvs",
            market_region: "nvs",
            user_lang: "nvs",
            game_feature: "nvs"
        };
        
        this.app.settings.languageChanged.connect(this._onLanguageChanged.bind(this));
        
    }

    public async init(): Promise<void>
    {
        
        if (Settings.isApp())
        {
            this.analyticsVersion = await this.getAnalyctsVersion();
            this.acpCoreVersion = await this.getACPCoreVersion();
            await this.initACPCore();
        }
        
        this.setupLocaleData();
    }
    
    public setupLocaleData()
    {
        var localeData = this.getMostSuitableCountryData();

        if(localeData){
            this.localeData = localeData;
            this.baseData.market = `${this.localeData.countryShort}|${this.localeData.country}`;
            this.baseData.market_region = this.localeData.region;
        }
        
        if(Settings.isApp()){
            this._setUserLang(this.app.settings.language ? `${this.app.settings.language}|${this.app.user.data.settings.languageChanged ? "settings" : "default"}` : "nvs");
            this.baseData.app_name = `${this.domainPrefix}|${(Device.android ? 'bmx champions|android' : 'bmx champions|ios')}`;
        }
        else{
            this._setUserLang(`${this.getMostSuitableLanguage()}|${this.app.user.data.settings.languageChanged ? "settings" : "default"}`);
            this.baseData.domain = `${this.domainPrefix}|${this.localeData.domain}`;
        }
        
    }
    
    private getMostSuitableLanguage(): string{
        let languageId = this.app.settings.language;
        //App uses set language, Web uses html _LANG_ value
        if(!Settings.isApp() && document.documentElement.lang && document.documentElement.lang != "_LANG_"){
            languageId = document.documentElement.lang;
            
        }
        //languageId = "en_ZA"
        return languageId;
    }
    
    private getMostSuitableCountryData(): CountryData | undefined
    {
        if(this.app.settings.language){
            let languageId = this.getMostSuitableLanguage();
            
            // Check if given id matches one of the available countries
            if(COUNTRY_VALUES[languageId]){
                return COUNTRY_VALUES[languageId];
            }

            // If given id does not match, check if at least first element matches one of the available languages
            // Example: en_US does not match exactly any of listed languages, but en_GB is the most suitable replacement
            languageId = this.getFirstElement(languageId);
            
            let countryKeys = Object.keys(COUNTRY_VALUES);

            for (const item of countryKeys)
            {
                if (item.startsWith(languageId))
                {
                    return COUNTRY_VALUES[item];
                }
            }
        }
        
        return undefined;
    }
    
    private getFirstElement(languageId: string): string
    {
        return (languageId.split('_').shift() as string).toLowerCase();
    }

    private async getAnalyctsVersion(): Promise<string>
    {
        if (!window.ACPAnalytics)
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] ACPAnalytics not available');

            return '';
        }

        return new Promise((resolve) =>
        {
            window.ACPAnalytics.extensionVersion((version: string) =>
            {
                if(this.logInfo) console.log(`[AnalyticsPlugin] ACPAnalytics version: ${version}`);
                resolve(version);
            }, (error: Error) =>
            {
                if(this.logInfo) console.log(`[AnalyticsPlugin] ERROR: Could not find ACPAnalytics version. ${error}`);
                resolve('');
            });
        });
    }

    private async getACPCoreVersion(): Promise<string>
    {
        if (!window.ACPCore)
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] ACPCore not available');

            return '';
        }

        return new Promise((resolve) =>
        {
            window.ACPCore.extensionVersion((version: string) =>
            {
                if(this.logInfo) console.log(`[AnalyticsPlugin] ACPCore version: ${version}`);
                resolve(version);
            }, (error: Error) =>
            {
                if(this.logInfo) console.log(`[AnalyticsPlugin] ERROR: Could not find ACPCore version. ${error}`);
                resolve('');
            });
        });
    }

    private async initACPCore(): Promise<void>
    {
        
        if (!window.ACPCore)
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] ACPCore not available');

            return;
        }

        if (this.acpInitialised) return;
        this.acpInitialised = true;

        // Set log level for analytics
        // window.ACPCore.setLogLevel(window.ACPCore.ACPMobileLogLevelError);
        // window.ACPCore.setLogLevel(window.ACPCore.ACPMobileLogLevelWarning);
        // window.ACPCore.setLogLevel(window.ACPCore.ACPMobileLogLevelDebug);
        window.ACPCore.setLogLevel(window.ACPCore.ACPMobileLogLevelVerbose);
        //window.ACPCore.configureWith(this.environmentID);
        
    }
    
    public trackEvent(event: AnalyticsData, data?: Partial<AnalyticsMetadata>): void
    {
        this.track(event, data, true);
    }
    
    public trackState(state: AnalyticsData, data?: Partial<AnalyticsMetadata>): void
    {
        if(this.currentState != state){
            this.currentState = state;
            this.track(state, data, false);
        }
        
    }

    private track(event: AnalyticsData, data?: Partial<AnalyticsMetadata>, isEvent?: boolean): void
    {
        // Update web version data
        if(!Settings.isApp())
        {
            //example: web|monday|2021-07-21|12:34:44|UTC+1
            let date = new Date(_BUILD_DATE_);
            
            const weekday = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
            const dayName = weekday[date.getDay()];
            
            const year = date.getFullYear().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            
            const fullDate = `${year}-${month}-${day}`;
            const time = `${hours}:${minutes}:${seconds}`;
            const timezoneOffset = date.getTimezoneOffset() / 60;
            const timezoneOffsetStr = `UTC${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`;
            
            this.baseData.version = `web|${dayName}|${fullDate}|${time}|${timezoneOffsetStr}`;
        }
        
        this.baseData.game_character = this.app.user.data.vehicle.character;
        
        this.baseData = { ...this.baseData, ...event.persistentMetaData};
        data = { ...this.baseData, ...event.additionalMetadata, ...data };

        if(data.race_number !== undefined && data.race_number != "nvs"){
            if(data.race_number == 0){
                data.race_number = "tutorial race";
                data.game_level = "tutorial level";
            }
            else{
                data.game_level = `race ${data.race_number}|level ${data.race_number}`;
                data.race_number = `race ${data.race_number}`;
            }
        }
        
        if(data.page_name && data.page_name != "nvs"){
            if(Settings.isApp()){
                if(this.localeData?.countryShort){
                    data.page_name = `${this.domainPrefix}|${this.localeData.countryShort}|bmx-champions|${data.page_name}`;
                }
                else{
                    data.page_name = `${this.domainPrefix}|nvs|bmx-champions|${data.page_name}`;
                }
                
            }
            else{
                data.page_name = `${this.localeData.domain}/games/bmx-champions/${data.page_name}`;
            }
            
        }
        
        // Convert all number metadata to lowercase string
        let key: keyof typeof data;
        for (key in data)
        {
            const metadata = data[key];

            if (typeof metadata != 'number'){
                (data[key] as string) = (data[key] as string).toLowerCase();
            }
            else{
                data[key] = data[key].toString();
            }
            
        }
        
        if (Settings.isApp())
        {
            if (!window.ACPCore || !this.acpInitialised)
            {
                if(this.logInfo) console.log('[AnalyticsPlugin] Cannot track because ACP was not found or initalised incorrectly', event.id, data);

                return;
            }

            // TODO: State event to be updated
            (isEvent) ? this._trackAction(event.id, data) : this._trackState(event.id, data);
        }
        else if (window.turner_metadata)
        {

            var type = (isEvent) ? (data.interaction ? 'interaction' : 'game') : 'pageview';

            window.turner_metadata.trackAction.push({
                type: type,
                data: data
            });
            if(this.logInfo) console.log('[AnalyticsPlugin] Send to Bootstrapper: ', event.id, data);
        }
        else
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] Cannot track because Bootstrapper has not been initalised', event.id, data);
        }
    }
    
    private _onLanguageChanged(language: string): void
    {
        this.setupLocaleData();
    }
    
    private _setUserLang(language: string): void
    {
        language = language.replace("en_GB", "en_UK");
        language = language.replace("ar_AR", "ar_AE");
        this.baseData.user_lang = language;
    }

    private _trackState(state: string, data: Partial<AnalyticsMetadata>): void
    {
        window.ACPCore.trackState(state, data, () =>
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] Track state success:', state);
        }, (e: Error) =>
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] Track state ERROR:', state, e);
        });
    }

    private _trackAction(action: string, data: Partial<AnalyticsMetadata>): void
    {

        window.ACPCore.trackAction(action, data, () =>
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] Track action success:', action);
        }, (e: Error) =>
        {
            if(this.logInfo) console.log('[AnalyticsPlugin] Track action ERROR:', action, e);
        });
    }
    
}
